<template>
  <div class="fts-settings-page">
    <!-- On the phone home the frame's bar and workspace card already say where you are. -->
    <header class="fts-settings-head" v-if="!compact">
      <div>
        <h1>Overview</h1>
        <p>{{ intro }}</p>
      </div>
    </header>

    <div class="fts-settings-loading" v-if="loading">
      <fts-spinner :size="16" />
      <span>Loading this workspace&rsquo;s plan&hellip;</span>
    </div>

    <template v-else-if="unavailable">
      <!-- States 8 / members: no plan data to show, and a reason rather than an empty page. -->
      <section class="fts-settings-card">
        <h2>{{ unavailable.title }}</h2>
        <p class="fts-settings-card-sub">{{ unavailable.body }}</p>
      </section>
    </template>

    <template v-else-if="member">
      <!-- State 7 (D-12): someone who does not administer the workspace. What it is on and
           where they stand in it; the plan, people and security pages are an admin's. -->
      <section class="fts-settings-card">
        <div>
          <h2>{{ workspaceName }}</h2>
          <p class="fts-settings-card-sub">{{ memberIntro }}</p>
        </div>
        <dl class="fts-settings-facts">
          <div class="fts-settings-fact" v-for="fact in memberFacts" :key="fact.key">
            <dt>{{ fact.label }}</dt>
            <dd>{{ fact.value }}</dd>
          </div>
        </dl>
        <div class="fts-settings-actions">
          <button type="button" class="fts-settings-btn" @click="goAccount">
            <font-awesome-icon :icon="['fas', 'user']" />
            Your account settings
          </button>
          <button type="button" class="fts-settings-btn" v-if="managesPeople" @click="goPeople">
            <font-awesome-icon :icon="['fas', 'user-friends']" />
            People
          </button>
        </div>
      </section>

      <!-- A member's way out. Admins find it in General; not on the phone home's status. -->
      <settings-leave-workspace v-if="!compact" :workspace-id="workspaceId" @left="$emit('left', $event)" />
    </template>

    <template v-else-if="summary">
      <section class="fts-settings-card" v-if="attention.length">
        <div style="display: flex; align-items: center; gap: 10px;">
          <h2>Needs attention</h2>
        </div>
        <div
          v-for="item in attention"
          :key="item.id"
          class="fts-settings-banner"
          :class="item.tone === 'danger' ? 'is-danger' : 'is-warn'"
        >
          <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
          <span>
            <strong>{{ item.title }}</strong>
            {{ item.body }}
          </span>
        </div>
      </section>

      <div class="fts-settings-grid-3">
        <section class="fts-settings-card">
          <div class="fts-settings-eyebrow">Plan</div>
          <div class="fts-settings-big">{{ planLabel }}</div>
          <div class="fts-settings-card-sub">{{ planLine }}</div>
          <a href="#" v-if="onlineBilling" @click.prevent="goPlan">See the plan</a>
        </section>

        <section class="fts-settings-card" v-if="seats">
          <div class="fts-settings-eyebrow">Actor seats</div>
          <div class="fts-settings-big">{{ seatsValue }}</div>
          <div class="fts-settings-card-sub">{{ seatsLine }}</div>
          <span class="fts-settings-note">
            Only actor seats can connect Claude, ChatGPT, n8n, Zapier or the API.
          </span>
        </section>

        <section class="fts-settings-card" v-if="onlineBilling">
          <div class="fts-settings-eyebrow">AI credits</div>
          <div class="fts-settings-big">{{ creditsValue }}</div>
          <div class="fts-settings-card-sub">{{ creditsLine }}</div>
          <span class="fts-settings-note">Credits never expire.</span>
        </section>
      </div>

      <section class="fts-settings-card" v-if="capacity">
        <div>
          <h2>Plan capacity</h2>
          <p class="fts-settings-card-sub">{{ capacityIntro }}</p>
        </div>

        <div class="fts-settings-capacity">
          <div class="fts-settings-capacity-cell" v-for="cell in capacityCells" :key="cell.key">
            <div class="fts-settings-capacity-label">
              <font-awesome-icon :icon="['fas', cell.icon]" />
              <span>{{ cell.label }}</span>
            </div>
            <div class="fts-settings-capacity-value">
              <span class="fts-settings-capacity-used">{{ cell.used }}</span>
              <span class="fts-settings-capacity-of">{{ cell.of }}</span>
            </div>
            <div class="fts-settings-meter" v-if="cell.pct !== null">
              <div
                class="fts-settings-meter-fill"
                :class="{ 'is-warn': cell.pct >= 80 }"
                :style="{ width: Math.min(100, cell.pct) + '%' }"
              ></div>
            </div>
            <div class="fts-settings-meter-none" v-else></div>
            <div class="fts-settings-capacity-note">{{ cell.note }}</div>
          </div>
        </div>
      </section>

      <!-- The page can reach main before the API that feeds it does: say so plainly
           rather than dropping the card and leaving a gap where capacity belongs. -->
      <section class="fts-settings-card" v-else>
        <div>
          <h2>Plan capacity</h2>
          <p class="fts-settings-card-sub">
            People, active records, file storage and change history appear here once this
            environment is running an API that reports them.
          </p>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
/**
 * Settings &rsaquo; Overview (alpha, P0 slice).
 *
 * Everything on this page is read from today's `GET api/billing/summary` — no new
 * endpoint. The capacity cells of the ruled design (people / records / storage /
 * history) need `summary.capacity`, which does not exist yet, so the page says so
 * rather than drawing numbers it cannot stand behind.
 *
 * Someone who does not administer the workspace gets state 7 instead, from
 * `GET api/Workspace/MemberSummary`: the plan by name, the owner and their own
 * two-step standing (plus head counts for a user admin) — nothing the billing
 * summary keeps for admins, which is why that one can stay admin-only.
 */
import { commonStore } from "../../store/commonStore";
import { clientStore } from "../../store/clientStore";
import { settingsRole, SettingsRole } from "../settingsAccess";
import FTSSpinner from "../../elements/FTSSpinner";
import SettingsLeaveWorkspace from "./SettingsLeaveWorkspace";

export default {
  name: "SettingsOverview",
  components: { "fts-spinner": FTSSpinner, "settings-leave-workspace": SettingsLeaveWorkspace },
  props: {
    workspaceId: [String, Number],
    /** The phone home's status: the page without its own heading (D-11, board 10). */
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      summary: null,
      member: null,
      unavailable: null,
    };
  },
  computed: {
    /** The role in THIS workspace (settingsAccess.js), which picks the page. */
    role() {
      return settingsRole();
    },
    adminView() {
      return this.role === SettingsRole.ADMIN;
    },
    intro() {
      if (!this.adminView) return "What this workspace is on, and where you stand in it.";
      // The vendor overview has no credits to speak of (spec §12.3).
      return this.summary && !this.onlineBilling
        ? "Where this workspace stands: who bills it, its seats and what it is using."
        : "Where this workspace stands: plan, seats, AI credits and anything that needs you.";
    },
    workspaceName() {
      return clientStore.getters.workspaceName || "This workspace";
    },
    managesPeople() {
      return this.role === SettingsRole.USER_ADMIN;
    },
    memberIntro() {
      return this.managesPeople
        ? "You manage people here. Workspace admins manage the plan and security."
        : "You are a member. Workspace admins manage the plan, people and security.";
    },
    memberFacts() {
      const m = this.member;
      if (!m) return [];
      const facts = [{ key: "plan", label: "Plan", value: this.memberPlan(m.plan) }];
      if (m.owner) facts.push({ key: "owner", label: "Owner", value: m.owner });
      // GNey is on in every workspace; the one switch is the one a workspace admin can flip per
      // person (pricing spec §11 R3). Running out of credits is the chat's top-up card, not this.
      let gney = "On for you";
      if (m.gneyDisabled) gney = "Switched off for you";
      facts.push({ key: "gney", label: "GNey", value: gney });
      facts.push({ key: "twoFactor", label: "Two-step verification", value: this.twoFactorLine(m.twoFactor) });
      // "People cards only" for a user admin (D-12); the server sends no counts to a member.
      if (m.people) {
        facts.push({ key: "people", label: "People", value: this.number(m.people.members) });
        facts.push({ key: "waiting", label: "Waiting to join", value: this.number(m.people.waiting) });
      }
      return facts;
    },
    seats() {
      return this.summary && this.summary.seats;
    },
    /**
     * False for the vendor overview (api/billing/vendor-overview, spec §12.3): a vendor-billed
     * workspace while online billing is off has seats and capacity to show, but no credits to
     * count and no plan page to go to.
     */
    onlineBilling() {
      return !!this.summary && this.summary.enabled !== false;
    },
    isManual() {
      return this.summary && this.summary.source === "manual";
    },
    planLabel() {
      return this.planLabelOf(this.summary);
    },
    planLine() {
      if (!this.summary) return "";
      if (this.isManual) {
        const by = this.summary.billedBy || "your vendor";
        return this.summary.validUntil
          ? `Billed directly by ${by} · valid until ${this.date(this.summary.validUntil)}`
          : `Billed directly by ${by}`;
      }
      if (this.summary.status === "past_due") return "Payment issue — see Billing";
      if (this.summary.currentPeriodEnd) {
        const verb = this.summary.cancelAtPeriodEnd ? "Cancels" : "Renews";
        return `${verb} on ${this.date(this.summary.currentPeriodEnd)}`;
      }
      return "No subscription";
    },
    seatsValue() {
      const s = this.seats;
      if (!s) return "";
      return s.seatLimit ? `${s.held} of ${s.seatLimit}` : String(s.held);
    },
    seatsLine() {
      const s = this.seats;
      if (!s) return "";
      const parts = [`${s.included} included`];
      if (s.addOnSlots != null) parts.push(`${s.addOnSlots} add-on slots`);
      if (s.extra > 0) parts.push(`${s.extra} extra`);
      return parts.join(" · ");
    },
    creditsValue() {
      if (!this.summary) return "";
      if (this.summary.uncapped) return "Unlimited";
      return this.number(this.balance);
    },
    balance() {
      const c = (this.summary && this.summary.credits) || {};
      return (c.plan || 0) + (c.purchased || 0);
    },
    creditsLine() {
      if (!this.summary) return "";
      const c = this.summary.credits || {};
      if (this.summary.uncapped) {
        return `usage billed by ${this.summary.billedBy || "your vendor"}`;
      }
      if (c.includedMonthly > 0) {
        return `${this.number(c.plan)} of ${this.number(c.includedMonthly)} this month, plus ${this.number(c.purchased)} bought`;
      }
      return "pay as you go";
    },
    capacity() {
      return this.summary && this.summary.capacity;
    },
    capacityIntro() {
      const plan = this.planLabel;
      return this.uncappedPlan
        ? `${plan} sets no capacity limits. These are what this workspace is using.`
        : `What ${plan} includes, and how much of it is in use.`;
    },
    uncappedPlan() {
      const c = this.capacity;
      if (!c) return false;
      // An arrangement outside the ladder reports no numbers at all - never zero ones.
      return c.people.included === null
        && c.activeRecords.included === null
        && c.storageGb.included === null;
    },
    capacityCells() {
      const c = this.capacity;
      if (!c) return [];
      return [
        this.cell("people", "People", "user-friends", c.people,
          v => this.number(v),
          "Adding people is never blocked.",
          "No limit on people."),
        this.cell("records", "Active records", "table-cells-large", c.activeRecords,
          v => this.number(v),
          "Going over never stops anyone working.",
          "No limit on records."),
        this.cell("storage", "File storage", "server", c.storageGb,
          v => `${this.decimals(v)} GB`,
          "Uploads are not blocked at this figure.",
          "No limit on storage."),
        {
          key: "history",
          label: "Change history",
          icon: "clock-rotate-left",
          used: this.historyLabel,
          of: c.historyDays ? "kept" : "",
          pct: null,
          note: "Records themselves are never removed.",
        },
      ];
    },
    historyLabel() {
      const days = this.capacity && this.capacity.historyDays;
      if (!days) return "Unlimited";
      if (days % 365 === 0) {
        const years = days / 365;
        return years === 1 ? "1 year" : `${years} years`;
      }
      // 1095 and 2555 are 3 and 7 years as the price list counts them.
      if (days >= 360) return `${Math.round(days / 365)} years`;
      return `${days} days`;
    },
    attention() {
      const out = [];
      if (!this.summary) return out;
      if (this.summary.status === "past_due") {
        out.push({
          id: "past_due",
          tone: "danger",
          title: "A payment did not go through",
          body: this.summary.graceUntil
            ? `AI keeps working until ${this.date(this.summary.graceUntil)}. Everything else is unaffected.`
            : "Update the card in Billing. Everything other than AI is unaffected.",
        });
      }
      // The vendor overview sells and counts no credits, so none can run out there.
      const metered = this.onlineBilling && !this.summary.uncapped;
      if (metered && this.balance <= 0) {
        out.push({
          id: "credits_out",
          tone: "warn",
          title: "No AI credits left",
          body: "GNey and AI steps are paused. Everything else in the workspace keeps working.",
        });
      } else if (metered && this.summary.lowCredit) {
        out.push({
          id: "credits_low",
          tone: "warn",
          title: "AI credits are running low",
          body: "Top up to keep GNey and automations running.",
        });
      }
      // A capacity limit in sight (W-10). Never a refusal: NOTHING in this build stops for
      // being over capacity -- not a record, not an upload, not an invite. The notes used to
      // say invites and uploads paused at the limit, which nothing does, so a customer would
      // have been warned about a wall that is not there.
      this.capacityCells
        .filter(x => x.pct !== null && x.pct >= 80)
        .forEach(x => {
          const over = x.pct > 100;
          out.push({
            id: `capacity_${x.key}`,
            tone: "warn",
            title: over
              ? `${x.label} is past what this plan includes`
              : `${x.label} is at ${Math.round(x.pct)}% of this plan`,
            body: `${x.used} of ${x.of.replace(/^of /, "")}. ${x.note}`,
          });
        });
      return out;
    },
  },
  methods: {
    /** A plan's name from either read: the billing summary or the member summary's plan. */
    planLabelOf(plan) {
      if (!plan) return "";
      if (plan.planName) return plan.planName;
      const key = plan.planKey;
      if (!key || key === "free") return "Free";
      if (key === "vendor") return "Billed by a partner";
      return key.charAt(0).toUpperCase() + key.slice(1);
    },
    memberPlan(plan) {
      // Where online billing is off the server claims nothing, so neither does the page
      // (state 8) -- a workspace paying by invoice is not "Free". Who bills it is still said
      // when that is a vendor company (pricing spec §12.3): the arrangement is with them.
      if (!plan || !plan.enabled) {
        return plan && plan.billedBy ? `Billed by ${plan.billedBy}` : "Arranged with Inistate";
      }
      if (plan.source === "manual" && !plan.planName) {
        return plan.billedBy ? `Billed by ${plan.billedBy}` : "Billed by a partner";
      }
      return this.planLabelOf(plan);
    },
    twoFactorLine(twoFactor) {
      if (!twoFactor) return "";
      if (twoFactor.required) return twoFactor.enrolled ? "Required, set up" : "Required, not set up yet";
      return twoFactor.enrolled ? "Set up" : "Not set up";
    },
    goAccount() {
      this.$router.push({ name: "settings", params: { section: "profile" } }).catch(() => {});
    },
    goPeople() {
      this.$router.push({ name: "settings", params: { section: "people" } }).catch(() => {});
    },
    async loadMember() {
      try {
        const res = await commonStore.state.client.get("/api/Workspace/MemberSummary", {
          params: { workspaceId: this.workspaceId },
        });
        this.member = res.data;
      } catch (err) {
        const status = err && err.response && err.response.status;
        this.member = null;
        // An API older than this page has no MemberSummary: say what a member can know.
        this.unavailable = status === 404
          ? {
            title: "Only a workspace admin can see the plan",
            body: "Ask a workspace admin if you need a change to the plan, seats or AI credits.",
          }
          : {
            title: "This workspace’s summary is not available right now",
            body: "Please try again in a moment.",
          };
      }
    },
    /**
     * One capacity dimension. `included: null` means the arrangement sets no cap,
     * so there is no meter and no percentage - never a full bar, which is what a
     * zero would draw for exactly the customers who are not on the ladder.
     */
    cell(key, label, icon, dim, format, note, uncappedNote) {
      const used = (dim && dim.used) || 0;
      const included = dim ? dim.included : null;
      const capped = included !== null && included !== undefined;
      return {
        key,
        label,
        icon,
        used: format(used),
        of: capped ? `of ${format(included)}` : "used",
        pct: capped && included > 0 ? (used / included) * 100 : null,
        note: capped ? note : uncappedNote,
      };
    },
    decimals(value) {
      // One decimal is the difference between "14.2 GB of 20" and a number that looks
      // like it is not moving; whole GB only once the figure is big enough not to care.
      const n = Number(value || 0);
      return n >= 100 ? Math.round(n).toLocaleString("en-US") : (Math.round(n * 10) / 10).toString();
    },
    number(value) {
      const n = Number(value || 0);
      return Math.abs(n) >= 100
        ? Math.round(n).toLocaleString("en-US")
        : (Math.round(n * 10) / 10).toLocaleString("en-US");
    },
    date(value) {
      if (!value) return "";
      const d = new Date(value);
      return isNaN(d.getTime())
        ? ""
        : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    },
    goPlan() {
      this.$router.push({ name: "settings", params: { section: "plan" } }).catch(() => {});
    },
    /** While online billing is off: a vendor-billed workspace's overview, or null for any other. */
    async vendorOverview() {
      try {
        const res = await commonStore.state.client.get("/api/billing/vendor-overview", {
          params: { workspaceId: this.workspaceId },
        });
        return res.data || null;
      } catch (err) {
        return null;
      }
    },
    async load() {
      this.loading = true;
      this.unavailable = null;
      this.summary = null;
      this.member = null;
      if (!this.adminView) {
        // Not theirs to ask the billing summary: it would only refuse them.
        await this.loadMember();
        this.loading = false;
        return;
      }
      try {
        const res = await commonStore.state.client.get("/api/billing/summary", {
          params: { workspaceId: this.workspaceId },
        });
        this.summary = res.data;
      } catch (err) {
        const status = err && err.response && err.response.status;
        this.summary = null;
        if (status === 403) {
          this.unavailable = {
            title: "Only a workspace admin can see the plan",
            body: "Ask a workspace admin if you need a change to the plan, seats or AI credits.",
          };
        } else if (status === 404) {
          // States 8: online billing is not switched on for this environment. A workspace a
          // vendor company bills still shows who bills it, its seats and its capacity (pricing
          // spec §12.3).
          this.summary = await this.vendorOverview();
          if (!this.summary) {
            this.unavailable = {
              title: "Your plan is arranged with Inistate",
              body: "Plans are not sold online here yet. Talk to us to change the plan; people, security and the rest of this workspace’s settings work as usual.",
            };
          }
        } else {
          this.unavailable = {
            title: "Plan information is not available right now",
            body: "Please try again in a moment.",
          };
        }
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    workspaceId() {
      this.load();
    },
  },
  created() {
    this.load();
  },
};
</script>
